// src/pages/PlantsPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { getProducts } from '../services/api';
// S3-01.3 follow-up (2026-08-30): ProductGrid IGNORES its products prop
// (self-fetches the WHOLE catalog with its own filter chrome) — it put 17
// frogs on this page the moment the first product of this type existed.
// Render ProductCards directly, the FrogsPage pattern.
import ProductCard from '../components/Products/ProductCard';
import { Container, Row, Col } from 'react-bootstrap';
import SEO from '../components/SEO';
import { PLANT_KEYWORDS } from '../constants/seoKeywords';
import { PLANTS_SHIP_WITH_ANIMALS_MESSAGE } from '../constants/shippingRules';
import { Info } from 'lucide-react';
import FroggySpinner from '../components/common/FroggySpinner';
import EmptyStateWayOn from '../components/common/EmptyStateWayOn';
import '../styles/products.css';
import '../styles/ff-empty-state.css';
import '../styles/plants-filters.css';

// I-03 S3-01.7 — the "All" chip's own key. A group label is free text,
// so no sentinel string is safe; null is.
const ALL_GROUPS = null;

// Batch 34 C4 (Cowork run 32): the group filter is the design system's
// TOGGLE, not a bespoke pill — a 36px / 8px-radius ff-btn, secondary at
// rest and PRIMARY when selected (the 2026-10-05 "selected state = primary
// fill" ruling), aria-pressed carrying the state. The filled bright-cyan
// 999px 44px pill was the last off-family control on the page. The
// `ff-plant-chip` hook stays for the strip's own layout (plants-filters.css).
export const plantChipClass = (selected) => (
  `ff-btn ${selected ? 'ff-btn--primary' : 'ff-btn--secondary'} ff-plant-chip`
);

const PlantsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distinguish "loaded, empty list" from "load failed" — 2026-07-02
  // fix identical to FrogsPage / GeckosPage. Prevents backend timeout
  // from being indistinguishable from actually being sold out.
  const [loadError, setLoadError] = useState(null);
  // I-03 S3-01.7 — group filter (operator eye test: "plants need
  // CATEGORIES — ferns, bromeliads, vining — to group and filter").
  // Client-side: the whole plant catalog is already on the page, so a
  // round trip per chip would be slower and would flicker.
  const [activeGroup, setActiveGroup] = useState(ALL_GROUPS);

  const fetchAvailablePlants = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const filters = {
        speciesType: 1, // PLANT
        isForSale: true,
      };
      const response = await getProducts(filters);
      setProducts(response);
    } catch (error) {
      console.error('[PlantsPage] load failed:', error?.message || error);
      setLoadError(
        error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')
          ? 'Having trouble reaching our inventory just now. Please try again in a moment.'
          : 'Something went wrong loading available plants. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailablePlants();
  }, []);

  // Chips come from the STOCK actually listed, not from a fixed list —
  // a group with nothing in it is not a filter, it is a dead end.
  // Case-insensitive de-dupe on first-seen spelling, matching the
  // server's own rule (store/views_cuttings._known_plant_groups).
  const groups = useMemo(() => {
    const seen = new Map();
    (products || []).forEach((p) => {
      const label = (p.plant_group || '').trim();
      if (!label) return;
      const key = label.toLowerCase();
      if (!seen.has(key)) seen.set(key, label);
    });
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [products]);

  const visibleProducts = useMemo(() => {
    if (!activeGroup) return products;
    const key = activeGroup.toLowerCase();
    return (products || []).filter(
      (p) => (p.plant_group || '').trim().toLowerCase() === key,
    );
  }, [products, activeGroup]);

  return (
    <>
      <SEO
        title="Vivarium & Terrarium Plants — Feeling Froggy"
        description="Vivarium and terrarium plants selected for dart frogs, mossy frogs, and tree frogs. Ready for bioactive enclosures."
        canonical="https://feelingfroggy.llc/plants"
        keywords={PLANT_KEYWORDS}
      />
      <Container className="products-page">
        <h1 className="ff-heading-h1">Available Now – Plants</h1>
        {/* Operator shipping rule (2026-08-31): no plant-only boxes.
            Batch 34 C5 (operator, 2026-10-07: "we don't want to hit them
            over the head with it… but it's worth telling them"): READABLE,
            not muted — --ff-text-secondary at 16px with a small info mark,
            under the h1. A line, not a warning box; never on each card. */}
        <p className="ff-plants-ride-along" data-plants-ride-along>
          <Info size={16} aria-hidden="true" className="ff-plants-ride-along__icon" />
          <span>{PLANTS_SHIP_WITH_ANIMALS_MESSAGE}</span>
        </p>
        {loading ? (
          <FroggySpinner size="lg" />
        ) : loadError ? (
          <div className="text-center py-5">
            <p className="text-muted mb-3">{loadError}</p>
            <button
              type="button"
              className="ff-btn ff-btn--secondary"
              onClick={fetchAvailablePlants}
            >
              Try again
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className="ff-empty-state">
            <img
              src="/logos/ff_original_96px.png"
              alt=""
              aria-hidden="true"
              className="ff-empty-state__logo"
            />
            <h3 className="ff-empty-state__title">We're building our Plant inventory</h3>
            <p className="ff-empty-state__message">Please check back soon for new arrivals.</p>
            <EmptyStateWayOn />
          </div>
        ) : (
          <>
            {groups.length > 0 && (
              <div
                className="ff-plant-filters"
                role="group"
                aria-label="Filter plants by group"
                data-testid="plant-group-filters"
              >
                <button
                  type="button"
                  className={plantChipClass(activeGroup === ALL_GROUPS)}
                  aria-pressed={activeGroup === ALL_GROUPS}
                  onClick={() => setActiveGroup(ALL_GROUPS)}
                >
                  {`All (${products.length})`}
                </button>
                {groups.map((g) => {
                  const count = products.filter(
                    (p) => (p.plant_group || '').trim().toLowerCase() === g.toLowerCase(),
                  ).length;
                  const active = activeGroup
                    && activeGroup.toLowerCase() === g.toLowerCase();
                  return (
                    <button
                      key={g}
                      type="button"
                      className={plantChipClass(!!active)}
                      aria-pressed={!!active}
                      onClick={() => setActiveGroup(active ? ALL_GROUPS : g)}
                    >
                      {`${g} (${count})`}
                    </button>
                  );
                })}
              </div>
            )}
            {visibleProducts.length === 0 ? (
              <p className="text-muted text-center">
                Nothing in that group right now.
              </p>
            ) : (
              <Row className="g-4">
                {visibleProducts.map((product, productIdx) => (
                  <Col key={product.id} xs={12} sm={6} md={4}>
                    <ProductCard product={product} eagerImage={productIdx < 6} />
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default PlantsPage;